import React, { useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, Button, FormControlLabel, MenuItem, Paper, Stack, Switch, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useLocale } from '../../contexts/LocaleContext';
import { useToast } from '../../contexts/ToastContext';
import { ConfirmDialog, DateField, EmptyState, FormDialog, LoadingState, NumberField, SectionCard, parseNumberInput } from '../ui';
import {
  cancelVendorSaleWindow,
  createVendorSaleWindow,
  getVendorSalePolicy,
  listVendorDealers,
  listVendorSaleWindowProducts,
  listVendorSaleWindows,
  setVendorSalePolicy,
  updateVendorSaleWindow,
  VENDOR_SALE_POLICY_QUERY_KEY,
  VENDOR_SALE_WINDOWS_QUERY_KEY,
  type BrandSalePolicy,
  type SaleWindowProduct,
  type SaleWindowProductList,
  type VendorDealerOption,
  type SaleWindowsApiError,
  type VendorSaleWindow,
} from '../../services/saleWindowsApi';
import {
  saleWindowChip,
  saleWindowDisplayStatus,
  saleWindowFloorText,
  saleWindowNarrows,
  saleWindowProductLabel,
  saleWindowProductsText,
  saleWindowRange,
} from './saleWindowShared';

const NS = 'vendor.saleWindows';
const ALL_DEALERS = '__all__';

type FloorKind = 'none' | 'price' | 'percent';
/** Whole brand, or the products named (MOT-370). */
type ProductScope = 'all' | 'some';

const EMPTY_FORM = {
  name: '',
  dealer: ALL_DEALERS,
  productScope: 'all' as ProductScope,
  products: [] as SaleWindowProduct[],
  startAt: '',
  endAt: '',
  floorKind: 'none' as FloorKind,
  floorValue: '',
  floorPrivate: false,
  note: '',
};

const toDateInput = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formFor = (w: VendorSaleWindow): typeof EMPTY_FORM => ({
  name: w.name,
  dealer: w.organizationId || ALL_DEALERS,
  productScope: w.productIds && w.productIds.length > 0 ? 'some' : 'all',
  products: w.products || [],
  startAt: toDateInput(w.startAt),
  endAt: toDateInput(w.endAt),
  floorKind: w.floorPrice != null ? 'price' : w.maxPercentBelowMap != null ? 'percent' : 'none',
  floorValue: w.floorPrice != null ? String(w.floorPrice) : w.maxPercentBelowMap != null ? String(w.maxPercentBelowMap) : '',
  floorPrivate: w.floorPrivate,
  note: w.note || '',
});

/**
 * Priya's sale windows: the dates inside which her brand's dealers may
 * advertise below MAP, for every verified dealer or for one named store. The
 * window feeds the MAP guards and the violation detector for those stores;
 * nothing is pushed to any storefront from here.
 */
export const VendorSaleWindowsTab: React.FC<{ readOnly?: boolean }> = ({ readOnly = false }) => {
  const { t } = useAppTranslation();
  const { formatCurrency } = useLocale();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<VendorSaleWindow | 'new' | null>(null);
  const [cancelling, setCancelling] = useState<VendorSaleWindow | null>(null);
  // Narrowing an existing window's product list reprices stores' listings, so the save is confirmed first (MOT-415).
  const [confirmNarrow, setConfirmNarrow] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  // The rule is per brand (MOT-388): windows are offered only once the brand says
  // sale pricing is allowed inside approved windows alone.
  const { data: policy, isLoading: policyLoading, isError: policyIsError, error: policyErr } = useQuery<BrandSalePolicy, SaleWindowsApiError>({
    queryKey: VENDOR_SALE_POLICY_QUERY_KEY,
    queryFn: getVendorSalePolicy,
  });
  const required = policy?.saleWindowsRequired === true;
  // A distributor carry or a vendor not yet approved has no brand policy to read or set, so
  // the policy route is a 403 for it. That is not a load failure: it keeps the tab it had
  // before the rule existed (its windows, read-only) with a notice, never the error alert.
  const policyUnavailable = policyIsError && policyErr?.status === 403;
  const policyError = policyIsError && !policyUnavailable;
  const showWindows = required || policyUnavailable;
  const locked = readOnly || policyUnavailable;
  const { data: windows, isLoading, isError } = useQuery<VendorSaleWindow[]>({
    queryKey: VENDOR_SALE_WINDOWS_QUERY_KEY,
    queryFn: listVendorSaleWindows,
    enabled: showWindows,
  });
  const setPolicy = useMutation({
    mutationFn: (next: boolean) => setVendorSalePolicy(next),
    onSuccess: (next) => {
      showToast(next.saleWindowsRequired ? t(`${NS}.policy.turnedOn`) : t(`${NS}.policy.turnedOff`), 'success');
      queryClient.invalidateQueries({ queryKey: VENDOR_SALE_POLICY_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: VENDOR_SALE_WINDOWS_QUERY_KEY });
    },
    onError: (e: any) => setError(e?.message || t(`${NS}.error`)),
  });
  const { data: dealers } = useQuery<VendorDealerOption[]>({
    queryKey: ['vendor-sale-window-dealers'],
    queryFn: listVendorDealers,
    enabled: editing !== null,
  });

  // The brand's catalog for the product picker, only while the window being edited names products.
  const { data: brandProducts, isLoading: productsLoading } = useQuery<SaleWindowProductList>({
    queryKey: ['vendor-sale-window-products'],
    queryFn: listVendorSaleWindowProducts,
    enabled: editing !== null && form.productScope === 'some',
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: VENDOR_SALE_WINDOWS_QUERY_KEY });
  const close = () => {
    setEditing(null);
    setConfirmNarrow(false);
    setForm(EMPTY_FORM);
    setError(null);
  };

  const payload = () => {
    const floor = parseNumberInput(form.floorValue);
    return {
      name: form.name.trim(),
      startAt: new Date(`${form.startAt}T00:00:00`).toISOString(),
      endAt: new Date(`${form.endAt}T23:59:59`).toISOString(),
      floorPrice: form.floorKind === 'price' ? floor : null,
      maxPercentBelowMap: form.floorKind === 'percent' ? floor : null,
      floorPrivate: form.floorKind !== 'none' && form.floorPrivate,
      note: form.note.trim() || null,
    };
  };

  const formProductIds = (): string[] | null => (form.productScope === 'some' ? form.products.map((p) => p.id) : null);
  const narrows = editing !== null && editing !== 'new' && saleWindowNarrows(editing.productIds, formProductIds());

  const save = useMutation({
    mutationFn: async (): Promise<number> => {
      if (editing === 'new') {
        await createVendorSaleWindow({
          ...payload(),
          organizationId: form.dealer === ALL_DEALERS ? null : form.dealer,
          productIds: formProductIds(),
        });
        return 0;
      }
      const window = editing as VendorSaleWindow;
      const productIds = formProductIds();
      // An unchanged list is left out, so the server keeps it as stored.
      const changed = saleWindowNarrows(window.productIds, productIds) || saleWindowNarrows(productIds, window.productIds);
      const result = await updateVendorSaleWindow(window.id, { ...payload(), ...(changed ? { productIds } : {}) });
      return result.overridesEnded;
    },
    onSuccess: (overridesEnded) => {
      showToast(overridesEnded > 0 ? t(`${NS}.savedEnded`, { count: overridesEnded }) : t(`${NS}.saved`), 'success');
      close();
      refresh();
    },
    onError: (e: any) => {
      setConfirmNarrow(false);
      setError(e?.message || t(`${NS}.error`));
      // A failed save may still have written part of it (the list is saved before
      // the removed products' prices are ended), so read the windows again.
      refresh();
    },
  });
  const submit = () => (narrows ? setConfirmNarrow(true) : save.mutate());
  const cancel = useMutation({
    mutationFn: (id: string) => cancelVendorSaleWindow(id),
    onSuccess: () => {
      showToast(t(`${NS}.cancelled`), 'success');
      setCancelling(null);
      refresh();
    },
    onError: (e: any) => {
      setCancelling(null);
      setError(e?.message || t(`${NS}.error`));
    },
  });

  const counts = useMemo(() => {
    const now = new Date();
    const statuses = (windows || []).map((w) => saleWindowDisplayStatus(w, now));
    return {
      active: statuses.filter((s) => s === 'active').length,
      upcoming: statuses.filter((s) => s === 'upcoming').length,
    };
  }, [windows]);

  const floorValid =
    form.floorKind === 'none' ||
    (parseNumberInput(form.floorValue) != null && (parseNumberInput(form.floorValue) as number) > 0 && (form.floorKind !== 'percent' || (parseNumberInput(form.floorValue) as number) <= 100));
  const productsValid = form.productScope === 'all' || form.products.length > 0;
  const valid = !!form.name.trim() && !!form.startAt && !!form.endAt && form.endAt >= form.startAt && floorValid && productsValid;

  if (policyLoading || (showWindows && isLoading)) return <LoadingState />;
  if (policyError || (showWindows && isError)) return <Alert severity="error">{t(`${NS}.loadError`)}</Alert>;

  const policyControl = policyUnavailable ? (
    <Alert severity="info" sx={{ mb: 2 }}>{t(`${NS}.policy.unavailable`)}</Alert>
  ) : (
    <Box sx={{ mb: 2 }}>
      <FormControlLabel
        control={<Switch checked={required} disabled={readOnly || setPolicy.isPending} onChange={(e) => setPolicy.mutate(e.target.checked)} />}
        label={t(`${NS}.policy.switch`, { brand: policy?.brandName || '' })}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {required ? t(`${NS}.policy.onHelp`) : t(`${NS}.policy.offHelp`)}
      </Typography>
    </Box>
  );

  if (!showWindows) {
    return (
      <SectionCard dense padded title={t(`${NS}.title`)} subtitle={t(`${NS}.policy.offSubtitle`)}>
        {policyControl}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary">{t(`${NS}.policy.offBody`)}</Typography>
      </SectionCard>
    );
  }

  const rows = [...(windows || [])].sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

  return (
    <SectionCard
      dense
      padded
      title={t(`${NS}.title`)}
      subtitle={t(`${NS}.coverage`, { active: counts.active, upcoming: counts.upcoming })}
      actions={
        !locked && (
          <Button size="small" variant="contained" onClick={() => { setForm(EMPTY_FORM); setError(null); setEditing('new'); }}>
            {t(`${NS}.add`)}
          </Button>
        )
      }
    >
      {policyControl}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t(`${NS}.intro`)}</Typography>
      {error && !editing && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {rows.length === 0 ? (
        <EmptyState title={t(`${NS}.emptyTitle`)} description={t(`${NS}.emptyDescription`)} size="compact" />
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            {rows.map((w) => {
              const status = saleWindowDisplayStatus(w);
              return (
                <Box key={w.id} sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2">{w.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {w.organizationId ? t(`${NS}.oneDealer`, { store: w.organizationName || t(`${NS}.unknownStore`) }) : t(`${NS}.allDealers`)} · {w.brandName}
                      </Typography>
                    </Box>
                    {saleWindowChip(status, t, NS)}
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {saleWindowRange(w.startAt, w.endAt)} · {saleWindowFloorText(w, t, NS, formatCurrency)}
                    {w.floorPrivate && (w.floorPrice != null || w.maxPercentBelowMap != null) ? ` · ${t(`${NS}.floor.privateMark`)}` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{saleWindowProductsText(w, t, NS)}</Typography>
                  {w.note && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{w.note}</Typography>}
                  {!locked && w.status === 'active' && status !== 'expired' && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                      <Button size="small" variant="outlined" onClick={() => { setForm(formFor(w)); setError(null); setEditing(w); }}>{t(`${NS}.edit`)}</Button>
                      <Button size="small" color="inherit" onClick={() => setCancelling(w)}>{t(`${NS}.cancel`)}</Button>
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Paper>
      )}

      <FormDialog
        open={editing !== null}
        title={editing === 'new' ? t(`${NS}.newTitle`) : t(`${NS}.editTitle`, { name: (editing as VendorSaleWindow | null)?.name || '' })}
        onClose={close}
        onSubmit={submit}
        submitLabel={editing === 'new' ? t(`${NS}.create`) : t(`${NS}.save`)}
        submitDisabled={!valid}
        loading={save.isPending}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">{t(`${NS}.dialogIntro`)}</Typography>
          <TextField label={t(`${NS}.name`)} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
          <TextField
            select
            label={t(`${NS}.appliesTo`)}
            value={form.dealer}
            onChange={(e) => setForm({ ...form, dealer: e.target.value })}
            helperText={editing === 'new' ? t(`${NS}.appliesToHelp`) : t(`${NS}.appliesToFixed`)}
            disabled={editing !== 'new'}
            fullWidth
          >
            <MenuItem value={ALL_DEALERS}>{t(`${NS}.allDealers`)}</MenuItem>
            {(dealers || []).map((d) => <MenuItem key={d.organizationId} value={d.organizationId}>{d.name}</MenuItem>)}
            {editing !== 'new' && editing && editing.organizationId && !(dealers || []).some((d) => d.organizationId === editing.organizationId) && (
              <MenuItem value={editing.organizationId}>{editing.organizationName || editing.organizationId}</MenuItem>
            )}
          </TextField>
          <TextField
            select
            label={t(`${NS}.productScope`)}
            value={form.productScope}
            onChange={(e) => setForm({ ...form, productScope: e.target.value as ProductScope, products: [] })}
            helperText={t(`${NS}.productScopeHelp`)}
            fullWidth
          >
            <MenuItem value="all">{t(`${NS}.productScopes.all`)}</MenuItem>
            <MenuItem value="some">{t(`${NS}.productScopes.some`)}</MenuItem>
          </TextField>
          {form.productScope === 'some' && (
            <Autocomplete
              multiple
              options={brandProducts?.products || []}
              value={form.products}
              loading={productsLoading}
              filterSelectedOptions
              isOptionEqualToValue={(a, b) => a.id === b.id}
              getOptionLabel={(p) => saleWindowProductLabel(p)}
              filterOptions={(options, state) => {
                const q = state.inputValue.trim().toLowerCase();
                const hits = q ? options.filter((p) => [p.name, p.model, p.sku].some((v) => v && v.toLowerCase().includes(q))) : options;
                return hits.slice(0, 50);
              }}
              onChange={(_, value) => setForm({ ...form, products: value })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(`${NS}.products.label`)}
                  placeholder={form.products.length === 0 ? t(`${NS}.products.placeholder`) : undefined}
                  helperText={t(`${NS}.products.help`)}
                />
              )}
            />
          )}
          {form.productScope === 'some' && brandProducts?.truncated && (
            <Alert severity="warning">{t(`${NS}.products.truncated`, { count: brandProducts.products.length })}</Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <DateField label={t(`${NS}.startAt`)} value={form.startAt} onChange={(v) => setForm({ ...form, startAt: v })} sx={{ width: '100%' }} />
            <DateField label={t(`${NS}.endAt`)} value={form.endAt} min={form.startAt || undefined} onChange={(v) => setForm({ ...form, endAt: v })} sx={{ width: '100%' }} />
          </Stack>
          <TextField
            select
            label={t(`${NS}.floorKind`)}
            value={form.floorKind}
            onChange={(e) => setForm({ ...form, floorKind: e.target.value as FloorKind, floorValue: '' })}
            helperText={t(`${NS}.floorHelp.${form.floorKind}`)}
            fullWidth
          >
            <MenuItem value="none">{t(`${NS}.floorKinds.none`)}</MenuItem>
            <MenuItem value="price">{t(`${NS}.floorKinds.price`)}</MenuItem>
            <MenuItem value="percent">{t(`${NS}.floorKinds.percent`)}</MenuItem>
          </TextField>
          {form.floorKind !== 'none' && (
            <>
              <NumberField
                label={form.floorKind === 'price' ? t(`${NS}.floorPrice`) : t(`${NS}.floorPercent`)}
                adornment={form.floorKind === 'price' ? '$' : '%'}
                min={0}
                max={form.floorKind === 'percent' ? 100 : undefined}
                step="0.01"
                value={form.floorValue}
                onChange={(v) => setForm({ ...form, floorValue: v })}
                sx={{ width: '100%' }}
              />
              <FormControlLabel
                control={<Switch checked={form.floorPrivate} onChange={(e) => setForm({ ...form, floorPrivate: e.target.checked })} />}
                label={t(`${NS}.floorPrivate`)}
              />
              <Typography variant="caption" color="text.secondary">{t(`${NS}.floorPrivateHelp`)}</Typography>
            </>
          )}
          <TextField label={t(`${NS}.note`)} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} multiline minRows={2} fullWidth />
          {narrows && <Alert severity="warning">{t(`${NS}.narrowNotice`)}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </FormDialog>

      <ConfirmDialog
        open={confirmNarrow}
        title={t(`${NS}.narrowTitle`, { name: (editing as VendorSaleWindow | null)?.name || '' })}
        message={t(`${NS}.narrowMessage`)}
        confirmLabel={t(`${NS}.narrowConfirm`)}
        confirmColor="warning"
        loading={save.isPending}
        onConfirm={() => save.mutate()}
        onClose={() => setConfirmNarrow(false)}
      />

      <ConfirmDialog
        open={!!cancelling}
        title={t(`${NS}.cancelTitle`, { name: cancelling?.name || '' })}
        message={t(`${NS}.cancelMessage`)}
        confirmLabel={t(`${NS}.cancelConfirm`)}
        confirmColor="error"
        loading={cancel.isPending}
        onConfirm={() => cancelling && cancel.mutate(cancelling.id)}
        onClose={() => setCancelling(null)}
      />
    </SectionCard>
  );
};
